/* fc-floating-support.js (rebuilt 2026-10-18): the support hub.
   Markup: sections/fc-floating-support.liquid (root #fc-flsup, one per page).
   A labelled launcher [data-flsup-cta] toggles the window [data-flsup-panel];
   inside, a tablist [data-flsup-tab] switches the views [data-flsup-view]
   (chat / email / track; the Email tab is hidden while the chat is on and
   appears once the customer reaches the Email view through the chat's
   "Email us" link, the Track tab's "Email the team" link or the hand-off).
   The chat body (snippets/fc-ai-chat.liquid +
   assets/fc-ai-chat.js) listens for the `fc:support:tab` event dispatched
   here. The Email view posts to the support backend's /api/contact (the
   root's data-fc-contact-endpoint, derived from the chat endpoint; the
   backend emails the team, see support-chat/lib/mail.ts) and falls back to
   Shopify's /contact form only when no backend is configured (that post is
   refused with "Missing CAPTCHA token" while reCAPTCHA is on, so the
   backend is the working path); it shows its success state in place. The
   Track view validates and
   then lets the browser GET the track-order page with ?order=&email=.
   Public API: window.fcSupport.open(tab) / close() / toggle() / isOpen() /
   select(tab). #support or #chat in the URL opens the window on load.
   Re-initialises on shopify:section:load for the theme editor. */
(function () {
  'use strict';
  var KEY_TAB = 'fcSupportTab';

  function initHub(root) {
    var launch = root.querySelector('[data-flsup-cta]');
    var win    = root.querySelector('[data-flsup-panel]');
    if (!launch || !win) return;
    var tabs  = Array.prototype.slice.call(root.querySelectorAll('[data-flsup-tab]'));
    var views = Array.prototype.slice.call(root.querySelectorAll('[data-flsup-view]'));
    var defaultTab = root.getAttribute('data-fc-default-tab') || 'email';
    var current = '';

    /* ── tabs ── */
    function hasTab(name) { return tabs.some(function (t) { return t.getAttribute('data-flsup-tab') === name; }); }
    function remembered() { try { return sessionStorage.getItem(KEY_TAB) || ''; } catch (e) { return ''; } }
    function remember(name) { try { sessionStorage.setItem(KEY_TAB, name); } catch (e) { /* private mode */ } }
    function isOpen() { return win.getAttribute('data-open') === 'true'; }
    function select(name, focusTab) {
      if (!hasTab(name)) name = hasTab(defaultTab) ? defaultTab : (tabs[0] ? tabs[0].getAttribute('data-flsup-tab') : '');
      if (!name) return;
      current = name;
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-flsup-tab') === name;
        if (on && t.hidden) t.hidden = false;   // Email tab is hidden while the chat is on until the customer gets there
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focusTab) { try { t.focus({ preventScroll: true }); } catch (e) {} }
      });
      views.forEach(function (v) { v.hidden = v.getAttribute('data-flsup-view') !== name; });
      root.setAttribute('data-fc-tab', name);
      remember(name);
      document.dispatchEvent(new CustomEvent('fc:support:tab', { detail: { tab: name, open: isOpen() } }));
    }

    /* ── open / close ── */
    function open(tab) {
      var name = tab || current || remembered() || defaultTab;
      win.setAttribute('data-open', 'true');
      win.setAttribute('aria-hidden', 'false');
      win.removeAttribute('inert');
      launch.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('fc-fls-open');
      select(name, false);
      var chatFocuses = name === 'chat' && window.matchMedia('(min-width: 600px)').matches;
      if (!chatFocuses) {
        // The chat script focuses its composer on wide screens; elsewhere move focus to the active tab.
        setTimeout(function () {
          var t = tabs.filter(function (x) { return x.getAttribute('aria-selected') === 'true'; })[0];
          if (t && isOpen()) { try { t.focus({ preventScroll: true }); } catch (e) {} }
        }, 80);
      }
    }
    function close(returnFocus) {
      if (!isOpen()) return;
      win.setAttribute('data-open', 'false');
      win.setAttribute('aria-hidden', 'true');
      win.setAttribute('inert', '');
      launch.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('fc-fls-open');
      if (returnFocus) { try { launch.focus({ preventScroll: true }); } catch (e) {} }
      document.dispatchEvent(new CustomEvent('fc:support:close'));
    }
    function toggle() { if (isOpen()) close(true); else open(); }

    window.fcSupport = { open: open, close: function () { close(false); }, toggle: toggle, isOpen: isOpen, select: function (n) { select(n, false); } };

    launch.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
    root.querySelectorAll('[data-flsup-close]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.stopPropagation(); close(true); });
    });
    root.querySelectorAll('[data-flsup-goto]').forEach(function (b) {
      b.addEventListener('click', function () { select(b.getAttribute('data-flsup-goto'), true); });
    });
    tabs.forEach(function (t) {
      t.addEventListener('click', function () { select(t.getAttribute('data-flsup-tab'), false); });
      t.addEventListener('keydown', function (e) {
        var vis = tabs.filter(function (x) { return !x.hidden; });
        var i = vis.indexOf(t), n = vis.length, j = i;
        if (i < 0 || !n) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % n;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + n) % n;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = n - 1;
        else return;
        e.preventDefault();
        select(vis[j].getAttribute('data-flsup-tab'), true);
      });
    });
    // Outside click closes the window on wide screens (on phones it is full-screen).
    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      var t = e.target;
      if (!t || !document.contains(t)) return;           // target removed by an inner handler
      if (root.contains(t)) return;
      if (window.matchMedia('(max-width: 599px)').matches) return;
      close(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close(true);
    });

    /* ── Email view: topic chips + fetch submit ── */
    var emailView  = root.querySelector('[data-flsup-view="email"]');
    var form       = emailView ? emailView.querySelector('form') : null;
    var submitBtn  = emailView ? emailView.querySelector('[data-flsup-submit]') : null;
    var submitLbl  = emailView ? emailView.querySelector('[data-flsup-submit-label]') : null;
    var errorBox   = emailView ? emailView.querySelector('[data-flsup-error]') : null;
    var formWrap   = emailView ? emailView.querySelector('[data-flsup-form-wrap]') : null;
    var successBox = emailView ? emailView.querySelector('[data-flsup-success]') : null;
    var topicInput = emailView ? emailView.querySelector('[data-flsup-topic-input]') : null;
    var topicChips = emailView ? Array.prototype.slice.call(emailView.querySelectorAll('[data-flsup-topic]')) : [];

    function showError(box, msg) { if (!box) return; box.textContent = msg || ''; box.setAttribute('data-show', 'true'); }
    function hideError(box) { if (box) box.removeAttribute('data-show'); }
    function setTopic(value) {
      topicChips.forEach(function (c) { c.setAttribute('aria-pressed', c.getAttribute('data-flsup-topic') === value && value ? 'true' : 'false'); });
      if (topicInput) topicInput.value = value || '';
    }
    topicChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var on = chip.getAttribute('aria-pressed') !== 'true';
        setTopic(on ? (chip.getAttribute('data-flsup-topic') || '') : '');
      });
    });
    function resetEmail() {
      if (form) {
        var emailEl = form.querySelector('input[type="email"]'), bodyEl = form.querySelector('textarea');
        if (bodyEl) bodyEl.value = '';
        if (emailEl && emailEl.value && emailEl.getAttribute('value') !== emailEl.value) emailEl.value = emailEl.getAttribute('value') || '';
      }
      setTopic('');
      hideError(errorBox);
      if (formWrap) formWrap.hidden = false;
      if (successBox) successBox.hidden = true;
    }
    root.querySelectorAll('[data-flsup-again]').forEach(function (b) {
      b.addEventListener('click', function () {
        resetEmail();
        var first = form && form.querySelector('textarea');
        if (first) { try { first.focus({ preventScroll: true }); } catch (e) {} }
      });
    });
    if (form && submitBtn) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (submitBtn.dataset.loading === 'true') return;
        var emailEl = form.querySelector('input[type="email"]');
        var bodyEl  = form.querySelector('textarea');
        if (!emailEl || !emailEl.value.trim() || !bodyEl || !bodyEl.value.trim()) {
          showError(errorBox, root.getAttribute('data-fc-msg-fill') || 'Please fill in both fields.');
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailEl.value.trim())) {
          showError(errorBox, root.getAttribute('data-fc-msg-email') || 'Please enter a valid email address.');
          return;
        }
        submitBtn.dataset.loading = 'true';
        if (submitLbl) submitLbl.textContent = submitLbl.dataset.labelSending || 'Sending…';
        hideError(errorBox);

        var endpoint = root.getAttribute('data-fc-contact-endpoint') || '';
        var request;
        if (endpoint) {
          // The backend emails the team (summary, page, and the chat transcript when there is one).
          var transcript = [];
          try { transcript = (JSON.parse(sessionStorage.getItem('fcChatLog') || '[]') || []).slice(-16).map(function (m) { return { role: m.role, content: String(m.content || '').slice(0, 2000) }; }); } catch (e) { transcript = []; }
          var topicEl = form.querySelector('[data-flsup-topic-input]');
          var payload = {
            email: emailEl.value.trim(),
            topic: topicEl ? topicEl.value : '',
            body: bodyEl.value.trim(),
            page: location.href,
            locale: root.getAttribute('data-fc-locale') || document.documentElement.lang || '',
            country: root.getAttribute('data-fc-country') || '',
            transcript: transcript
          };
          request = fetch(endpoint, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) })
            .then(function (r) { if (!r.ok) throw new Error('Send failed: ' + r.status); return r.text(); });
        } else {
          var fd = new FormData(form);
          if (!fd.has('form_type')) fd.set('form_type', 'contact');   // Shopify expects these on contact submissions
          fd.set('utf8', '✓');
          request = fetch('/contact', { method: 'POST', body: fd, headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' } })
            .then(function (r) { if (!r.ok) throw new Error('Send failed: ' + r.status); return r.text(); });
        }
        request
          .then(function () {
            submitBtn.removeAttribute('data-loading');
            if (submitLbl) submitLbl.textContent = submitLbl.dataset.labelSend || 'Send message';
            if (formWrap) formWrap.hidden = true;
            if (successBox) { successBox.hidden = false; try { successBox.focus({ preventScroll: true }); } catch (e) {} }
          })
          .catch(function (err) {
            submitBtn.removeAttribute('data-loading');
            if (submitLbl) submitLbl.textContent = submitLbl.dataset.labelSend || 'Send message';
            showError(errorBox, root.getAttribute('data-fc-msg-fail') || "Couldn't send your message. Please try again or email us directly.");
            console.error('[fc-floating-support] contact send failed:', err);
          });
      });
    }

    /* ── Track view: validate, then let the browser GET the track-order page ── */
    var trackForm = root.querySelector('[data-flsup-track]');
    if (trackForm) {
      var trackErr = trackForm.querySelector('[data-flsup-track-error]');
      trackForm.addEventListener('submit', function (e) {
        var o = trackForm.querySelector('[name="order"]'), m = trackForm.querySelector('[name="email"]');
        var order = o ? o.value.trim() : '', email = m ? m.value.trim() : '';
        if (!order || !email) { e.preventDefault(); showError(trackErr, root.getAttribute('data-fc-msg-track') || 'Please enter your order number and email.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { e.preventDefault(); showError(trackErr, root.getAttribute('data-fc-msg-email') || 'Please enter a valid email address.'); return; }
        if (o) o.value = order.replace(/^#/, '');
        if (m) m.value = email;
        hideError(trackErr);
      });
    }

    /* ── deep links: #support opens the window, #chat opens the Chat tab ── */
    try {
      var h = (location.hash || '').toLowerCase();
      if (h === '#chat' && hasTab('chat')) open('chat');
      else if (h === '#support' || h === '#help') open();
    } catch (e) { /* non-fatal */ }

    // Mark body so third-party chat widgets can keep clear of the launcher (CSS)
    document.body.classList.add('fc-flsup-active');
  }

  function init() {
    var root = document.getElementById('fc-flsup');
    if (!root || root.getAttribute('data-fc-init') === '1') return;
    root.setAttribute('data-fc-init', '1');
    initHub(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#fc-flsup')) init();
  });
})();
